/**
 * Feature registry — the single declarative table that maps every
 * `TierFeature` literal to its complete metadata: display name, error
 * code, downgrade behaviour, gate key, marketing copy.
 *
 * This is the framework primitive. Every enforcement layer reads from
 * here:
 *
 *   - Route middleware (`requireFeature`) — error code, gate key, upgrade copy
 *   - Service guards (`assertFeatureAllowed`) — error code, telemetry payload
 *   - Background jobs (`shouldSkipForTierDetailed`) — registry lookup for tier minimum
 *   - Frontend (`useCanUseFeature`) — display name, docs URL
 *   - Stripe webhook (downgrade) — `onDowngrade` strategy per feature
 *   - Architecture test — asserts every `TierFeature` literal has an entry
 *
 * Adding a new gated feature is two edits:
 *   1. New `TierFeature` literal in `policy.ts` + the per-tier boolean
 *   2. New `FEATURE_REGISTRY` entry here
 *
 * The architecture test fails CI if step 2 is forgotten.
 *
 * Adding a new tier (e.g. Enterprise) is one new entry in TIER_POLICY
 * + setting the `features` map per tier. The registry is unchanged
 * because tier-presence is read from TIER_POLICY at runtime, not
 * hardcoded here.
 */
import { TIER_POLICY } from './policy';
/**
 * THE registry. One entry per `TierFeature` literal — the architecture
 * test asserts no key is missing.
 */
export const FEATURE_REGISTRY = {
    phi: {
        key: 'phi',
        displayName: 'Restricted mode (HIPAA-ready)',
        description: 'restrict a workspace to BAA-covered processing (HIPAA-ready)',
        errorCode: 'PHI_TIER_REQUIRED',
        // Fail closed: the tier gates TURNING ON restricted mode, never keeping
        // it. `settings.phi` switches protections on (BAA-only egress, blocked
        // non-BAA clients), so clearing it on a downgrade left PHI already in
        // the workspace un-protected, and no resume or re-upgrade switched it
        // back (#3580). The flag stays; only an admin turns it off.
        onDowngrade: 'keep_visible',
    },
    publicSharing: {
        key: 'publicSharing',
        displayName: 'Public share links',
        description: 'share any note, protocol or artifact via a public link',
        errorCode: 'PUBLIC_SHARING_TIER_REQUIRED',
        // Existing links keep working; new ones blocked. Webhook handler
        // optionally revokes — for now we preserve until explicitly revoked.
        onDowngrade: 'preserve_read',
    },
    prioritySupport: {
        key: 'prioritySupport',
        displayName: 'Priority support',
        description: 'get same-day responses to support tickets',
        errorCode: 'PRIORITY_SUPPORT_TIER_REQUIRED',
        // Marketing-only today — no enforcement site, behaviour is "keep visible".
        onDowngrade: 'keep_visible',
    },
    inferredRelationships: {
        key: 'inferredRelationships',
        displayName: 'Inferred AI context graph',
        description: 'let Bower discover relationships between your captures automatically',
        errorCode: 'INFERRED_RELATIONSHIPS_TIER_REQUIRED',
        // Existing inferred edges stay; new inference is skipped.
        onDowngrade: 'keep_visible',
    },
    personalMemory: {
        key: 'personalMemory',
        displayName: 'Personal preferences memory',
        description: 'save personal notes that Bower remembers across every chat',
        errorCode: 'PERSONAL_MEMORY_TIER_REQUIRED',
        // Available on every tier — a downgrade never removes it, so data always stays.
        onDowngrade: 'keep_visible',
    },
    workspaceInstructions: {
        key: 'workspaceInstructions',
        displayName: 'Workspace instructions',
        description: 'set shared instructions that Bower uses for every member',
        errorCode: 'WORKSPACE_INSTRUCTIONS_TIER_REQUIRED',
        // Available on every tier — a downgrade never removes it, so content is
        // never soft-deleted (was TEAM-only + soft_delete before 2026-07-14).
        onDowngrade: 'keep_visible',
    },
    knowledgeGraph: {
        key: 'knowledgeGraph',
        displayName: 'Workspace visualizer',
        description: 'explore how your workspace connects as a 2D / 3D graph',
        errorCode: 'KNOWLEDGE_GRAPH_TIER_REQUIRED',
        // Available on every tier (2026-07-27) — a downgrade never removes it,
        // so the graph always stays readable (was PRO+ / preserve_read before).
        onDowngrade: 'keep_visible',
    },
    trashRestore: {
        key: 'trashRestore',
        displayName: 'Trash with 30-day restore',
        description: 'recover deleted artifacts for 30 days after deletion',
        errorCode: 'TRASH_RESTORE_TIER_REQUIRED',
        // Available on every tier (2026-10-01), so a downgrade never removes it
        // (was PRO+ / preserve_read before).
        onDowngrade: 'keep_visible',
    },
    privacyControls: {
        key: 'privacyControls',
        displayName: 'Per-artifact privacy controls',
        description: 'mark individual artifacts and collections as PRIVATE',
        errorCode: 'PRIVACY_CONTROLS_TIER_REQUIRED',
        // Available on every tier since 2026-07-26, so no downgrade can remove it
        // and the create/edit gate can never fire. Was `preserve_read`, which
        // described a transition that can no longer happen.
        onDowngrade: 'keep_visible',
    },
    workspaceRoles: {
        key: 'workspaceRoles',
        displayName: 'Workspace role granularity',
        description: 'assign Owner / Admin / Member / Viewer roles within a workspace',
        errorCode: 'WORKSPACE_ROLES_TIER_REQUIRED',
        // Existing role rows preserved; new role-changes gated.
        onDowngrade: 'preserve_read',
    },
    complianceLockdown: {
        key: 'complianceLockdown',
        displayName: 'Compliance lockdown',
        description: 'enable "Just me" mode for workspace-wide compliance lockdown',
        errorCode: 'COMPLIANCE_LOCKDOWN_TIER_REQUIRED',
        // Every tier since 2026-07-26 — see `privacyControls` above.
        onDowngrade: 'keep_visible',
    },
};
/**
 * Lowest tier in the ladder that turns `featureKey` on. Re-exported
 * from `policy.ts`; thin wrapper so call sites can import the
 * registry and helper together without two imports. Returns null
 * when no tier provides the feature.
 */
export function minimumTierFor(featureKey) {
    const ladder = ['FREE', 'PRO', 'TEAM'];
    return ladder.find((t) => TIER_POLICY[t].features[featureKey]) ?? null;
}
/**
 * Whether `tier` is at or above the feature's minimum. Single helper
 * used everywhere — `policy.canUseFeature`, route middleware, service
 * guards, background-job skip, frontend hook all delegate here.
 */
export function tierIncludesFeature(tier, featureKey) {
    return TIER_POLICY[tier].features[featureKey];
}
/**
 * The full registration for `key`. Throws if missing — but the
 * architecture test guarantees every TierFeature has an entry, so
 * this is effectively total.
 */
export function getFeatureRegistration(key) {
    const entry = FEATURE_REGISTRY[key];
    if (!entry) {
        throw new Error(`Missing FEATURE_REGISTRY entry for "${key}". Add it to packages/shared/src/billing/feature-registry.ts.`);
    }
    return entry;
}
//# sourceMappingURL=feature-registry.js.map